import { Pagination, PaginationItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import { usePagination, useTable } from "react-table";
import { SkeletonUi } from "../../reusableComponents/Skeleton/SkeletonUi";
import "./style.scss";

// ✅ Empty Component
const EmptyComponent = () => (
  <div className="empty-data">
    <p>No data available.</p>
  </div>
);

const ReactTable = ({
  columns,
  data = [],
  setParams,
  params,
  isLoading,
  variant = 1,
}) => {
  return (
    <>
      {variant === 1 ? (
        <Table
          columns={columns}
          data={data}
          setParams={setParams}
          params={params}
          isLoading={isLoading}
        />
      ) : (
        variant === 2 && (
          <NormalTable
            columns={columns}
            data={data}
            setParams={setParams}
            params={params}
            isLoading={isLoading}
          />
        )
      )}
    </>
  );
};

export default ReactTable;

const Table = ({ columns, data, setParams, params, isLoading }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: data?.results || [],
    });

  return (
    <>
      <div className="table_main">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              Array(5)
                .fill()
                .map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    {columns.map((_, colIndex) => (
                      <td key={`skeleton-cell-${colIndex}`}>
                        <SkeletonUi width="100%" height="20px" />
                      </td>
                    ))}
                  </tr>
                ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyComponent />
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                const value = row.original.status;
                return (
                  <tr
                    className={`${
                      value === "delete" || value === "blocked"
                        ? "notActive_tr"
                        : value === "In Progress"
                        ? "progress_tr"
                        : "active_tr"
                    }`}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {Math.ceil(data?.count / params?.page_size) > 1 && (
        <div className="pagination-container">
          <Pagination
            count={Math.ceil(data?.count / params?.page_size)}
            page={params?.page}
            onChange={(e, page) => {
              setParams((prevParams) => ({ ...prevParams, page }));
            }}
            size="small"
            showFirstButton
            showLastButton
            shape="rounded"
            renderItem={(item) => {
              const isPageItem = item.type === "page";
              return (
                <PaginationItem
                  {...item}
                  component="button"
                  label={
                    isPageItem && item.page < 10
                      ? `0${item.page}`
                      : item.page
                  }
                />
              );
            }}
            className="pagination"
          />
        </div>
      )}
    </>
  );
};

function NormalTable({ columns, data, isLoading }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    gotoPage,
    pageOptions,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
    },
    usePagination
  );

  const [currentPage, setCurrentPage] = useState(pageIndex + 1);

  useEffect(() => {
    setCurrentPage(pageIndex + 1);
  }, [pageIndex]);

  return (
    <>
      <div className="table_main">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {isLoading ? (
              Array(5)
                .fill()
                .map((_, index) => (
                  <tr key={`skeleton-${index}`}>
                    {columns.map((_, colIndex) => (
                      <td key={`skeleton-cell-${colIndex}`}>
                        <SkeletonUi width="100%" height="20px" />
                      </td>
                    ))}
                  </tr>
                ))
            ) : page.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyComponent />
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                const value = row.original.status;
                return (
                  <tr
                    className={`${
                      value === "delete" || value === "blocked"
                        ? "notActive_tr"
                        : value === "In Progress"
                        ? "progress_tr"
                        : "active_tr"
                    }`}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageOptions.length > 1 && (
        <div className="pagination-container">
          <Pagination
            count={pageOptions.length}
            page={currentPage}
            onChange={(e, page) => {
              gotoPage(page - 1);
              setCurrentPage(page);
            }}
            size="small"
            showFirstButton
            showLastButton
            shape="rounded"
            renderItem={(item) => (
              <PaginationItem
                {...item}
                component="button"
                label={
                  item.type === "page" && item.page < 10
                    ? `0${item.page}`
                    : item.page
                }
              />
            )}
            className="pagination"
          />
        </div>
      )}
    </>
  );
}
